import { useId, useRef, useState, type ButtonHTMLAttributes, type ReactNode, type Ref } from "react";
import { cn } from "../../../cn.ts";
import { Tooltip } from "../../../overlays/Tooltip/Tooltip.tsx";
import { ariaKeyShortcuts } from "../../display/Kbd/keys.ts";
import { Spinner } from "../../display/Spinner/Spinner.tsx";
import { buttonVariants, type ButtonSize, type ButtonVariant } from "../Button/Button.tsx";
import { describedByOf, withoutActivation } from "../unavailable.tsx";

/**
 * § Primitives, IconButton: the Spectrum UI Loading Button at its square `icon` size
 * (`@spectrumui/loading-button-dependencies`), square at the density's control height, whose
 * accessible name is the required `label`, with a tooltip carrying the same text
 * (§ Iconography: the tooltip is never the only place the name exists).
 *
 * `collapsible` (2026-09-27, by the owner's meeting decision on toolbars that run out of room): the
 * button draws its label beside the icon, as a labelled `Button` does, and a host's container query
 * collapses it to the square icon button when the toolbar it sits in is too narrow for its words
 * (§ Page composition, Collection pages and layouts). The host's rule hides the `button-label` slot
 * with `display: none` and squares the button; the accessible name stays `label` in both forms, and
 * the tooltip opens only while the label is collapsed, since a tooltip repeating words already on
 * screen is noise.
 *
 * `shortcut` (2026-10-01, the meeting page's player and transcript search) is the key that runs the
 * button, as `Button`'s is: an icon button has no label to draw it after, so it is drawn in the
 * tooltip after the label through the tooltip's own `shortcut` (§ Key hints, 2026-10-15), quiet, in
 * the platform's glyphs, and is the button's `aria-keyshortcuts`.
 *
 * Disabled (2026-10-10): a disabled icon button is `aria-disabled` rather than natively disabled, stays
 * in the focus order and under the pointer and runs nothing (`unavailable.tsx`), because a natively
 * disabled button fires no pointer event and takes no focus, which killed the tooltip that carries its
 * name. `disabledReason` says why it cannot be pressed: the tooltip then carries the label and, under
 * it, the reason, which is also the button's accessible description (`aria-describedby` naming a hidden
 * span of it inside the button, joined to any description the host names, as `Button`'s), and a
 * collapsible button opens it even while its label is drawn, since the reason is on screen nowhere
 * else. A loading button is natively disabled while it works, as `Button` is.
 */
export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children" | "aria-label"> {
  readonly icon: ReactNode;
  readonly label: string;
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly loading?: boolean;
  /** Draws the label beside the icon until the host's container rule collapses it. */
  readonly collapsible?: boolean;
  /** The key that runs the button, as the shortcut map writes it ("Shift+Enter"); the host binds it. */
  readonly shortcut?: string;
  /** Why the button cannot be pressed while `disabled`: in its tooltip under the label, and its accessible description; `null` is no reason at the moment. */
  readonly disabledReason?: string | null | undefined;
  readonly ref?: Ref<HTMLButtonElement>;
}

/** A label that draws no box is collapsed (the host's rule gives it `display: none`). */
function collapsed(label: HTMLElement | null): boolean {
  return label === null || label.getClientRects().length === 0;
}

export function IconButton({ icon, label, variant = "ghost", size = "md", loading = false, collapsible = false, shortcut, disabled, disabledReason, type = "button", className, ref, "aria-describedby": describedBy, ...rest }: IconButtonProps) {
  const labelRef = useRef<HTMLSpanElement>(null);
  const reasonId = useId();
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const unavailable = disabled === true && !loading;
  const reason = unavailable ? (disabledReason ?? undefined) : undefined;
  const glyph = loading ? (
    <Spinner size="sm" label={label} className="text-current" />
  ) : (
    <span className="inline-flex" aria-hidden="true">
      {icon}
    </span>
  );
  return (
    <Tooltip
      content={
        reason === undefined ? (
          label
        ) : (
          <span className="flex flex-col gap-(--space-1)">
            {label}
            <span data-slot="button-disabled-reason">{reason}</span>
          </span>
        )
      }
      {...(shortcut === undefined ? {} : { shortcut })}
      {...(collapsible
        ? {
            open: tooltipOpen,
            onOpenChange: (next: boolean) => {
              setTooltipOpen(next && (reason !== undefined || collapsed(labelRef.current)));
            },
          }
        : {})}
    >
      <button
        ref={ref}
        type={type}
        data-slot="button"
        data-variant={variant}
        data-size={size}
        data-icon-only={collapsible ? undefined : "true"}
        data-collapsible={collapsible ? "true" : undefined}
        className={cn(buttonVariants({ variant, size, iconOnly: !collapsible }), className)}
        aria-label={label}
        aria-busy={loading || undefined}
        disabled={loading}
        aria-disabled={unavailable || loading ? true : undefined}
        aria-describedby={describedByOf(describedBy, reason === undefined ? undefined : reasonId)}
        {...(shortcut === undefined ? {} : { "aria-keyshortcuts": ariaKeyShortcuts(shortcut) })}
        {...(unavailable ? withoutActivation(rest) : rest)}
      >
        {glyph}
        {collapsible && (
          <span ref={labelRef} data-slot="button-label" aria-hidden="true">
            {label}
          </span>
        )}
        {reason !== undefined && (
          <span id={reasonId} hidden>
            {reason}
          </span>
        )}
      </button>
    </Tooltip>
  );
}
